import { useEffect, useMemo } from "react";
import uniqBy from "lodash.uniqby";

import { botsConfigActions, teamActions, userSelectors } from "@fitness-app/app-store";
import { PermissionKey, PermissionScope, UserRole, type UserRoleEnum } from "@fitness-app/data-models";
import { CURRENT_ASSIGNED_TRAINER } from "@fitness-app/data-models/contants/currentAssignedTrainer";
import { TeamMemberRole, TeamMemberStatus } from "@fitness-app/data-models/entities/TeamMember";

import { useUserRole } from "~/hooks/trainer/useUserRole";
import { usePermission } from "~/hooks/usePermission";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

export const useAuthorOptions = (
  model?: Partial<{ authorId?: string; authorName?: string; authorRole?: UserRoleEnum }>,
  withAssignedTrainerOption = false,
  withCurrentClient = true,
) => {
  const teamTrainers = useAppSelector((store) => store.team.trainers);
  const clientData = useAppSelector((store) => store.trainee.profile);
  const userDetails = useAppSelector((store) => store.user.profile);
  const bots = useAppSelector((store) => store.botsConfig.list);
  const parentTrainerId = useAppSelector(userSelectors.getParentTrainerId);
  const { isTeamMember } = useUserRole();
  // Wskazanie cudzej trenerki jako autora wiadomości to pisanie w imieniu
  // całego zespołu — wcześniej wisiało na roli ACCOUNT_OWNER.
  const canWriteAsAnyone = usePermission(PermissionKey.CHAT_WRITE, PermissionScope.ALL);
  const dispatch = useAppDispatch();

  useEffect(() => {
    void dispatch(botsConfigActions.fetchChatBots());
    void dispatch(teamActions.fetchTeamMembers());
  }, [dispatch]);

  const authorOptions = useMemo(() => {
    const authors: {
      value: string;
      label: string;
      avatarUrl: string | null;
      role: UserRoleEnum;
    }[] = [];

    if (!userDetails) {
      return [];
    }

    const user = {
      value: userDetails.id,
      label: `${userDetails.firstName} ${userDetails.lastName}`,
      avatarUrl: userDetails.avatarUrl,
      role: userDetails.role || UserRole.TRAINER,
    };
    authors.push(user);

    const botsWithCapabilities = bots.filter((bot) => bot.capabilities.canSendMessagesToAnyRoom);

    if (botsWithCapabilities.length) {
      botsWithCapabilities.forEach((bot) => {
        authors.unshift({
          value: bot.id,
          label: bot.name,
          role: UserRole.BOT,
          avatarUrl: bot.icon || null,
        });
      });
    }

    if (
      withCurrentClient &&
      canWriteAsAnyone &&
      clientData?.assignedTrainer &&
      clientData?.assignedTrainer?.userId !== userDetails.id
    ) {
      const assignedUserId = clientData.assignedTrainer.userId;
      const assignedMember = teamTrainers.find((trainer) => trainer.userId === assignedUserId);
      // A concrete author is accepted only as an active, enabled team member (TRE-2285); a user
      // id with no such row (deleted or not in the team) would be refused. Without a user id
      // the option is the CURRENT_ASSIGNED_TRAINER sentinel, which the backend resolves itself.
      const isAcceptedAuthor =
        !assignedUserId || (assignedMember?.status === TeamMemberStatus.ACTIVE && !assignedMember.disabled);

      if (isAcceptedAuthor) {
        authors.push({
          value: assignedUserId || CURRENT_ASSIGNED_TRAINER,
          label: `${clientData.assignedTrainer.firstName} ${clientData.assignedTrainer.lastName}`,
          role: assignedMember?.role === TeamMemberRole.ACCOUNT_OWNER ? UserRole.TRAINER : UserRole.TEAM_MEMBER,
          avatarUrl: assignedMember?.user?.avatarUrl || null,
        });
      }
    }

    if (
      model?.authorId &&
      !authors.some((author) => author.value === model.authorId) &&
      isTeamMember &&
      model.authorId !== parentTrainerId
    ) {
      authors.push({
        value: model.authorId,
        label: model.authorName ?? model.authorId,
        role: model.authorRole ?? UserRole.TRAINER,
        avatarUrl: null,
      });
    }

    if (withAssignedTrainerOption) {
      authors.unshift({
        value: CURRENT_ASSIGNED_TRAINER,
        label: "Aktualny trener przypisany do klienta",
        role: UserRole.TRAINER,
        avatarUrl: null,
      });
    }

    if (canWriteAsAnyone && !withCurrentClient && withAssignedTrainerOption && !model) {
      teamTrainers.forEach((trainer) => {
        // Same rule the backend checks at send and delivery time (TRE-2285): only active,
        // enabled members, and the account owner writes as TRAINER, not TEAM_MEMBER.
        if (
          trainer.userId !== userDetails.id &&
          trainer.userId &&
          trainer.selectable &&
          trainer.status === TeamMemberStatus.ACTIVE &&
          !trainer.disabled
        ) {
          authors.push({
            value: trainer.userId,
            label: `${trainer.firstName} ${trainer.lastName}`,
            role: trainer.role === TeamMemberRole.ACCOUNT_OWNER ? UserRole.TRAINER : UserRole.TEAM_MEMBER,
            avatarUrl: trainer.user?.avatarUrl || null,
          });
        }
      });
    }

    return uniqBy(authors, "value");
  }, [
    userDetails,
    canWriteAsAnyone,
    model,
    parentTrainerId,
    bots,
    teamTrainers,
    clientData,
    isTeamMember,
    withAssignedTrainerOption,
    withCurrentClient,
  ]);

  return authorOptions;
};
